// Inline assessment capture — replaces the old standalone /assessments page (removed) so this
// lives directly in the engagement's workspace card, matching "everything via the workspace card,
// not a separate page." Ports that page's status/upload/text-capture logic and adds the segment
// (manual measurement), voice-note, and text-note actions.
//
// PropertyMeasurementsSection/PropertyNotesSection (the property-wide versions) live in their own
// standalone spot on the property detail page (see UXView.tsx), but are hidden there while
// assessment_scheduled — this card's own action row (Enter manually / Add photo / Add voice note
// / Add note / Upload report) is the only place to capture measurements/photos/notes for that one
// status, so there's exactly one entry point instead of two duplicate ones.
import { useEffect, useRef, useState } from 'react';
import WorkspaceMeetingCard from '@/components/Meetings/WorkspaceMeetingCard';
import { useDeleteAssessmentSegmentMutation, type Segment } from '@/redux/internal-services/assessmentsApi';
import {
  useGetCurrentEngagementAssessmentQuery,
  useGetAssessmentTemplateByCodeQuery,
  useCreateAssessmentMutation,
  useUploadMeasurementReportMutation,
  useGenerateAssessmentSummaryMutation,
  useSetAssessmentFieldMutation,
  useSetMeasurementOverrideMutation,
  useClearMeasurementOverrideMutation,
  type MeasurementField,
  type AssessmentField,
  type MeasurementProvenance,
  assessmentsApi,
} from '@/redux/internal-services/assessmentsApi';
import {
  useListNotesByContextQuery, useCreateNoteMutation, useUpdateNoteMutation, useDeleteNoteMutation,
  useUploadNoteAudioMutation, type Note,
} from '@/redux/internal-services/notesApi';
import {
  useUploadMediaFileMutation, useAttachPropertyMediaMutation, useUpdateMediaMutation, useDeleteMediaMutation,
  useGetMeetingsListQuery, useGetPropertyMediaQuery,
  type TaskFull,
} from '@/redux/internal-services/workspaceApiExtensions';
import { socketService } from '@/services/SocketService';
import { VoiceNoteRecorder } from '@/components/notes/VoiceNoteRecorder';
import { SegmentEntryForm, ManualSegmentList } from '@/components/Assessments/SegmentEntryForm';
import { Button } from '@/components/UI/Button';
const IcoCamera = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5 shrink-0" aria-hidden><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>;
const IcoFolderOpen = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5 shrink-0" aria-hidden><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>;
const IcoUpload = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5 shrink-0" aria-hidden><polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/></svg>;
const IcoRuler = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5 shrink-0" aria-hidden><path d="M17 3H7L3 7v10l4 4h10l4-4V7z"/><line x1="8" y1="12" x2="8" y2="12.01"/><line x1="12" y1="8" x2="12" y2="8.01"/><line x1="12" y1="16" x2="12" y2="16.01"/><line x1="16" y1="12" x2="16" y2="12.01"/></svg>;
const IcoMic = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5 shrink-0" aria-hidden><rect x="9" y="2" width="6" height="12" rx="3"/><path d="M5 11a7 7 0 0 0 14 0M12 18v3"/></svg>;
const IcoFileText = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5 shrink-0" aria-hidden><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>;
import { useTranslation } from 'react-i18next';

import { useAppDispatch } from '@/hooks/useAppDispatch';
import { workflowEngineApi } from '@/redux/internal-services/workflowEngineApi';

const OPEN_TASK_STATUSES_EXCLUDED = new Set(['completed', 'done', 'cancelled']);

// Single-select classification applied to a photo at upload time — freeform on the backend
// (media/models.py's `classifications: list[str]` has no enum), this is just this card's own
// fixed shortlist rather than the full property-wide MEDIA_CLASSIFICATIONS list (UXView.tsx),
// which is aimed at the general Photos section, not a quick assessment capture.
const PHOTO_TAGS = ['roof face', 'damage', 'elevation', 'other'] as const;

// Same idea as PHOTO_TAGS above — a fixed quick-select shortlist for voice/text notes captured
// during the assessment, rather than a freeform tag field. custom_tags itself stays a plain
// string[] on the backend (no enum), so this is purely a UI convenience list for this card.
const NOTE_TAGS = ['scope', 'access & safety', 'customer request'] as const;

const DEFAULT_TEMPLATE_CODE = 'ROOF-ASPH-ASMT';

// Purely a progress-bar width per REAL backend stage (see SummaryGenerationStage) — the stage
// itself is genuine, reported by the running job; only the exact percent within a stage is a
// display choice, since the backend doesn't (and can't cheaply) report finer-grained progress
// inside e.g. the multi-source reconciliation call.
const GENERATION_STAGE_PROGRESS: Record<string, number> = {
  QUEUED: 8, PARSING: 38, VALIDATING: 68, COMPOSING: 90,
};

// A background job's process can die without ever writing FAILED (confirmed live: a dev-mode
// server restart mid-job — unrelated file edits triggering uvicorn --reload — silently orphaned
// one, leaving `summary_generation.status` stuck at PARSING forever with no error recorded). Real
// jobs update `updated_at` at every stage change; anything further stale than this comfortably
// exceeds the "up to a couple of minutes" the pipeline itself documents, so it's treated as
// failed client-side rather than leaving the user staring at a progress bar that will never move
// again with no way to retry (the button stays hidden the whole time isGenerating is true).
// No client-side staleness guess any more. There used to be a 6-minute GENERATION_STALE_MS here
// which treated a run as failed once it stopped reporting progress for that long — because the
// frontend could not tell "still working" from "the worker died".
//
// It can now, and it does not have to guess: generation is a durable job (assessment_summary_jobs)
// claimed under a lease, so the BACKEND knows. It marks FAILED with the real error once attempts
// are exhausted, and a job orphaned by a restart is reclaimed automatically. On top of that the
// worker pushes `assessment:summary_finished` the moment a run ends, so the result arrives without
// waiting for a poll at all.
//
// Deleting the guess was not cosmetic. A measured transcript-only run takes ~3 minutes
// (reconciliation alone is ~130s at reasoning_effort=high), so a real run with a report and photos
// plausibly crossed 6 minutes and got labelled "stalled" while it was working perfectly — and
// since a superseded run is now cancelled, hitting "Try again" on that false alarm killed a run
// that was nearly finished.

// generated_summary.provenance is keyed by the vendored roof_pipeline's OWN field paths
// ("roof.total_area_sqft", "sections[0].pitch"), which don't read as a field name a contractor
// would recognize — this maps them to the same plain labels `summary.py::_compile_measurement_text`
// already uses server-side for these exact keys, so a value's citation and the value itself are
// described the same way in both places.
const PROVENANCE_FIELD_LABELS: Record<string, string> = {
  'roof.total_area_sqft': 'Total roof area',
  'roof.pitch': 'Predominant pitch',
  'roof.stories': 'Number of stories',
  'roof.facets': 'Total roof facets',
  'roof.material': 'Roofing material',
  'roof.ridge_length_ft': 'Total ridge length',
  'roof.valley_length_ft': 'Total valley length',
  'roof.eave_length_ft': 'Total eave length',
  'roof.rake_length_ft': 'Total rake length',
  'roof.hip_length_ft': 'Total hip length',
  'roof.wall_flashing_length_ft': 'Total wall flashing length',
  'roof.step_flashing_length_ft': 'Total step flashing length',
  'roof.transition_length_ft': 'Total transition length',
  'roof.parapet_wall_length_ft': 'Total parapet wall length',
  'roof.unspecified_length_ft': 'Total unspecified length',
};

function provenanceFieldLabel(key: string): string {
  if (PROVENANCE_FIELD_LABELS[key]) return PROVENANCE_FIELD_LABELS[key];
  const sectionMatch = key.match(/^sections\[(\d+)\]\.(.+)$/);
  if (sectionMatch) {
    const planeNo = Number(sectionMatch[1]) + 1;
    const sub = sectionMatch[2] === 'area_sqft' ? 'area' : sectionMatch[2].replace(/_/g, ' ');
    return `Roof plane ${planeNo} — ${sub}`;
  }
  const damageMatch = key.match(/^damage\[(\d+)\]\.(.+)$/);
  if (damageMatch) {
    return `Damage note — ${damageMatch[2].replace(/_/g, ' ')}`;
  }
  return key.replace(/_/g, ' ');
}

// The citation line ("Roofr Report.pdf · p.6 · line 5") — whatever combination of file/page/
// line/timestamp the source actually carries; falls back to source_type when a source has none
// of those (e.g. a manually entered segment has no file/page to cite).
function provenanceCitation(p: MeasurementProvenance): string {
  const parts = [
    p.file,
    p.page != null ? `p.${p.page}` : null,
    p.line != null ? `line ${p.line}` : null,
    p.timestamp,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : (p.source_type || 'source not recorded');
}


type Panel = 'manual' | 'upload' | 'voice' | 'text' | 'photo' | null;

// A field the Assessment Summary can show/edit, generalized over the two field kinds that
// happen to share the same shape (MeasurementField/AssessmentField) — 'measurement' fields are
// segment-consolidated (edits go through setMeasurementOverride, see EditableField below),
// 'assessment' fields are set directly (setAssessmentField already existed for these).
interface FieldSpec {
  field_code: string;
  field_name: string;
  uom?: string;
  data_type?: string;
  allowed_values: unknown[];
  kind: 'measurement' | 'assessment';
}

function toFieldSpec(f: MeasurementField | AssessmentField, kind: FieldSpec['kind']): FieldSpec {
  return {
    field_code: f.field_code, field_name: f.field_name, uom: f.uom,
    data_type: f.data_type, allowed_values: f.validation?.allowed_values ?? [], kind,
  };
}

/** One editable row in the Assessment Summary — a real template field (not a hardcoded label),
 *  pre-filled with its current value, saved on blur/Enter. Numeric fields get a number input,
 *  fields with a fixed allowed_values list get a <select>, everything else gets free text.
 *  `blockedMessage` (this field's own BLOCK validation message, if any) highlights the row red
 *  so "what's still missing" is visible right where the user fixes it, not just in a separate
 *  list above. */
const NUMERIC_TYPES = new Set(['INTEGER', 'DECIMAL']);

function EditableField({
  spec, value, blockedMessage, overridden, onSave, onReset, disabled,
}: {
  spec: FieldSpec;
  value: unknown;
  /** This field's own BLOCK validation message, if any — shown as an explanation line under the
   *  row (not just a red highlight) so "why is this wrong" is answered right where it's fixed. */
  blockedMessage?: string;
  /** True when this is a measurement field currently held at a hand-typed value rather than the
   *  segment-computed one (ConsolidatedValue.value_origin === 'MANUAL_OVERRIDE') — shows the
   *  "reset to computed" affordance. Always undefined/false for assessment fields. */
  overridden?: boolean;
  onSave: (value: unknown) => Promise<void>;
  onReset?: () => Promise<void>;
  disabled?: boolean;
}) {
  const isNumeric = NUMERIC_TYPES.has(spec.data_type ?? '');
  // An uncaptured MANDATORY numeric field reads as "0" — the same number its own validation
  // message already states ("value 0 is below the minimum 1.0") — rather than a blank box that
  // doesn't tell you what's actually missing. Purely a display default: nothing is written until
  // the user actually edits (see `touched` below), so just tabbing past an empty field saves
  // nothing.
  const currentStr = value != null ? String(value) : (isNumeric ? '0' : '');
  const [draft, setDraft] = useState(currentStr);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  // Shown on the row itself. The card's own error line sits above the whole summary, which can be
  // a long scroll away from the field that actually failed.
  const [failed, setFailed] = useState(false);
  useEffect(() => { setDraft(currentStr); setTouched(false); setFailed(false); }, [currentStr]);

  const commit = async () => {
    if (!touched) return;
    const raw = draft.trim();
    let parsed: unknown = raw === '' ? null : raw;
    if (spec.data_type === 'INTEGER') parsed = raw === '' ? null : parseInt(raw, 10);
    else if (spec.data_type === 'DECIMAL') parsed = raw === '' ? null : Number(raw);
    setTouched(false);
    if (parsed === value || (parsed == null && value == null)) return;
    if (typeof parsed === 'number' && Number.isNaN(parsed)) return;
    setBusy(true);
    setFailed(false);
    try {
      await onSave(parsed);
    } catch {
      setFailed(true);
      // The write did NOT happen, so the box must stop claiming it did — put back what the server
      // still holds. The message is the caller's job (it owns `t` and the card's error line); this
      // only has to make sure a rejected save is not indistinguishable from a successful one.
      // Swallowed after that: the caller has already reported it, and rethrowing here would
      // surface as an unhandled rejection from the `void commit()` in onBlur.
      setDraft(currentStr);
    } finally {
      setBusy(false);
    }
  };

  const isEnum = spec.allowed_values.length > 0;

  return (
    <div className={`py-1${blockedMessage ? ' rounded-md bg-danger/10 px-1.5' : ''}`}>
      <div className="flex items-center justify-between gap-2">
        <span className={`text-xs ${blockedMessage ? 'font-medium text-danger-text' : 'text-ui-text2'}`}>
          {spec.field_name}
        </span>
        <div className="flex items-center gap-1">
          {isEnum ? (
            <select
              value={draft}
              onChange={(e) => { if (!disabled) { setDraft(e.target.value); setTouched(true); } }}
              onBlur={() => { if (!disabled) void commit(); }}
              disabled={busy || disabled}
              className="min-w-[7rem] max-w-[11rem] rounded border border-ui-border bg-ui-input px-1.5 py-0.5 text-right text-xs text-ui-text focus:outline-none focus:border-ui-ring disabled:opacity-60"
            >
              <option value="">—</option>
              {spec.allowed_values.map((v) => (
                <option key={String(v)} value={String(v)}>{String(v)}</option>
              ))}
            </select>
          ) : (
            <input
              value={draft}
              onChange={(e) => { if (!disabled) { setDraft(e.target.value); setTouched(true); } }}
              onBlur={() => { if (!disabled) void commit(); }}
              onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
              onFocus={(e) => e.currentTarget.select()}
              disabled={busy || disabled}
              placeholder="—"
              inputMode={isNumeric ? 'decimal' : 'text'}
              className="w-24 rounded border border-ui-border bg-ui-input px-1.5 py-0.5 text-right text-xs text-ui-text placeholder:text-ui-text3 focus:outline-none focus:border-ui-ring disabled:opacity-60"
            />
          )}
          {spec.uom && <span className="text-xs text-ui-text3">{spec.uom}</span>}
          {overridden && onReset && (
            <button
              type="button"
              title="Reset to the computed value"
              onClick={() => void onReset()}
              className="text-ui-text3 hover:text-ui-text"
            >
              ↺
            </button>
          )}
          {busy && <span className="text-xs text-ui-text3">…</span>}
        </div>
      </div>
      {blockedMessage && <p className="mt-0.5 text-xs text-danger-text">⛔ {blockedMessage}</p>}
      {failed && (
        <p className="mt-0.5 text-xs text-danger-text">
          Not saved — the value above is what is still on record. Try again.
        </p>
      )}
    </div>
  );
}

interface AssessmentCaptureCardProps {
  engagementId: string;
  /** Needed only to attach an uploaded photo to the right property record (same
   *  property_media collection the Photos section elsewhere uses) — see handleUploadPhoto. */
  propertyId?: string;
  /** Engagement-scoped tasks (WorkflowCard already fetches these) — folded into this card's
   *  collapsed summary as an open-task count, so "Assessment meeting & capture" is one combined
   *  entry point instead of three separate things a Sales Agent has to check. */
  tasks?: TaskFull[];
  /** Bumped by a sibling module (e.g. the Estimate & proposal prep card's "Open assessment card"
   *  link) to force this card open and into view — any change to the number re-expands it, even
   *  if the value itself carries no meaning beyond "changed". */
  assessmentOpenSignal?: number;
  /** True when this card is only reachable via "Open assessment card" (the engagement has moved
   *  past assessment_scheduled, so this module is no longer natively bound to the current status
   *  — see WorkflowCard.tsx's lazy-append comment). Capture is done by this point, so only the
   *  read-only generated summary is worth showing — not the meeting scheduler, the capture action
   *  row, or the pre-compile validation header, all of which are about the capture step itself. */
  readOnly?: boolean;
  /** Start open, skipping the one-line "tap to expand" entry. For the modal the Estimate card's
   *  "Open assessment card" opens: the overlay already IS the disclosure, and `assessmentOpenSignal`
   *  cannot do it there — that only fires on a CHANGE, and a card mounted fresh inside a
   *  just-opened modal has no previous value to differ from. */
  initiallyExpanded?: boolean;
  /** Suppress this card's own "tap to expand" header. The modal that opens it already carries a
   *  title and a close button, and repeating the title inside the frame just states it twice. */
  hideHeader?: boolean;
}


export function AssessmentCaptureCard({
  engagementId, propertyId, tasks, assessmentOpenSignal, readOnly, initiallyExpanded, hideHeader,
}: AssessmentCaptureCardProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  // No poll — the backend already pushes `assessment:summary_finished` the instant a Generate
  // Summary background job ends (see AssessmentSummaryWorker._publish_summary_event), so this
  // relies on that push rather than re-fetching on a timer. `required_measurement`'s governance
  // gate lives in workflowEngineApi, a SEPARATE createApi() slice with its own cache, so a job
  // finishing here doesn't refresh "Complete Assessment" over there on its own (the
  // immediate-response mutations already handle this via assessmentsApi's own
  // invalidateWorkflowGates helper — this covers the DELAYED completion that helper can't see,
  // since it only runs once, at the QUEUED response).
  //
  // Invalidates rather than merging the payload: the event carries only the outcome, and the
  // assessment it refers to has a whole recomputed summary, measurement_values and validation
  // state to fetch. Trusting a socket payload to reconstruct that would be a second source of
  // truth for the same record.
  const { data: assessment, isLoading } = useGetCurrentEngagementAssessmentQuery(engagementId);
  useEffect(() => {
    const onFinished = (payload: unknown) => {
      const p = payload as { assessment_id?: string } | undefined;
      // A user can have more than one engagement open; only refresh for this card's own.
      if (p?.assessment_id && assessment?.assessment_id && p.assessment_id !== assessment.assessment_id) return;
      // The current-assessment query provides { type: 'Assessment', id: assessment_id } when it
      // resolved, and 'AssessmentList' when there was none — invalidate whichever applies.
      dispatch(assessmentsApi.util.invalidateTags([
        assessment?.assessment_id
          ? { type: 'Assessment' as const, id: assessment.assessment_id }
          : 'AssessmentList',
      ]));
      dispatch(workflowEngineApi.util.invalidateTags([
        { type: 'AvailableTransitions', id: engagementId },
        { type: 'EngagementWorkflowState', id: engagementId },
      ]));
    };
    socketService.on('assessment:summary_finished', onFinished);
    return () => socketService.off('assessment:summary_finished', onFinished);
  }, [assessment?.assessment_id, dispatch, engagementId]);

  const prevGenerationStatus = useRef<string | undefined>(undefined);
  useEffect(() => {
    const status = assessment?.summary_generation?.status;
    if (status === 'DONE' && prevGenerationStatus.current !== 'DONE') {
      dispatch(workflowEngineApi.util.invalidateTags([
        { type: 'AvailableTransitions', id: engagementId },
        { type: 'EngagementWorkflowState', id: engagementId },
      ]));
    }
    prevGenerationStatus.current = status;
  }, [assessment?.summary_generation?.status, dispatch, engagementId]);
  // Same query AssessmentMeetingDisplayCard (WorkflowCard.tsx) uses to find the assessment
  // meeting by context tag — folded in here so the meeting lives inside this one combined card
  // instead of rendering as its own separate module above it (WorkflowCard skips the standalone
  // meeting module when this capture card is also active, to avoid showing it twice).
  // `meeting_type: 'assessment'` is load-bearing, not decoration. Without it this took the most
  // recent meeting of ANY type on the engagement, so a later proposal-review or follow-up
  // meeting displayed as the assessment's own time inside a card titled "Assessment meeting &
  // capture". The backend's own lookup for the same thing already filters this way — see
  // WorkflowService (app/domain/workflows/service.py, context_type="engagement",
  // meeting_type="assessment") — so this brings the card in line with it rather than inventing
  // a rule.
  const { data: meetingsRes } = useGetMeetingsListQuery(
    {
      context_type: 'engagement', context_entity_id: engagementId, meeting_type: 'assessment',
      sort_by: 'start_time', sort_dir: -1, limit: 1,
    },
  );
  const meeting = meetingsRes?.data?.[0];
  const { data: propertyMedia = [] } = useGetPropertyMediaQuery(propertyId as string, { skip: !propertyId });
  // Real template field metadata (field_name/uom/data_type/allowed_values/requirement_level) for
  // the Assessment Summary's editable rows — never hardcode which fields exist or their labels;
  // this tenant's own template is the only source of truth for what "mandatory" means here.
  const { data: template } = useGetAssessmentTemplateByCodeQuery(
    assessment ? { code: assessment.assessment_template_code, version: assessment.assessment_template_version } : { code: '' },
    { skip: !assessment },
  );
  const [createAssessment] = useCreateAssessmentMutation();
  const [uploadReport, { isLoading: isUploading }] = useUploadMeasurementReportMutation();
  const [uploadMediaFile, { isLoading: isUploadingPhoto }] = useUploadMediaFileMutation();
  const [attachMedia] = useAttachPropertyMediaMutation();
  const [generateSummary, { isLoading: isGeneratingSummary }] = useGenerateAssessmentSummaryMutation();
  const [setAssessmentField] = useSetAssessmentFieldMutation();
  const [setMeasurementOverride] = useSetMeasurementOverrideMutation();
  const [clearMeasurementOverride] = useClearMeasurementOverrideMutation();
  const [updateNote] = useUpdateNoteMutation();
  const [deleteNote] = useDeleteNoteMutation();
  const [uploadNoteAudio, { isLoading: isTranscribing }] = useUploadNoteAudioMutation();
  const [updateMedia] = useUpdateMediaMutation();
  const [deleteMedia] = useDeleteMediaMutation();

  const [panel, setPanel] = useState<Panel>(null);
  // Which hand-entered segment the manual panel is editing (null = entering a new one). Declared
  // with the other top-level state: this component early-returns while the assessment loads, so a
  // hook added further down would change hook ORDER between renders.
  const [editingSegment, setEditingSegment] = useState<Segment | null>(null);
  const [segmentMenuOpen, setSegmentMenuOpen] = useState<string | null>(null);
  // Assessment Summary's own Edit/Done toggle — Required fields above is ALWAYS editable (it's
  // the actionable checklist), but the summary's other groups (linear measurements, flashing &
  // transitions, ...) default to a compact read-only display and only show OPTIONAL fields that
  // already have a value. Clicking Edit switches every row in those groups to an input AND adds
  // a row for any field that had no value yet (so it can be added, not just corrected).
  const editingSummary = !readOnly;
  // "Sources & reasoning" — collapsed by default, since a real compile can carry a dozen-plus
  // per-field citations (see below); most visits just want the numbers, not the paper trail.
  const [showProvenance, setShowProvenance] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteTag, setNoteTag] = useState('');
  const [voiceTag, setVoiceTag] = useState('');
  const [photoTag, setPhotoTag] = useState<string>(PHOTO_TAGS[0]);
  const [photoFilter, setPhotoFilter] = useState<string | null>(null);
  const [lightboxPhotoId, setLightboxPhotoId] = useState<string | null>(null);
  const [showReportPreview, setShowReportPreview] = useState(false);
  const [confirmDeletePhotoId, setConfirmDeletePhotoId] = useState<string | null>(null);
  const [confirmDeleteNoteId, setConfirmDeleteNoteId] = useState<string | null>(null);
  const [transcribeErrorId, setTranscribeErrorId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [summaryError, setSummaryError] = useState('');
  const [autoCreateError, setAutoCreateError] = useState('');
  const [autoCreating, setAutoCreating] = useState(false);
  // Whole-card collapse — this capture tool used to always render fully open, which is a lot of
  // UI (action row, textarea, generate-summary output) to show on a card that's opened constantly
  // just to check status. Starts collapsed behind a one-line summary, same "tap to expand" pattern
  // WorkspaceMeetingCard already uses for the sibling meeting-display card.
  const [expanded, setExpanded] = useState(!!initiallyExpanded);
  const [createNote, { isLoading: isSavingNote }] = useCreateNoteMutation();
  const [deleteSegment, { isLoading: isRemovingSegment }] = useDeleteAssessmentSegmentMutation();
  const rootRef = useRef<HTMLDivElement>(null);
  const prevOpenSignal = useRef(assessmentOpenSignal);
  useEffect(() => {
    if (assessmentOpenSignal !== undefined && assessmentOpenSignal !== prevOpenSignal.current) {
      prevOpenSignal.current = assessmentOpenSignal;
      setExpanded(true);
      rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [assessmentOpenSignal]);

  // Auto-start the (single, default) assessment the moment this card mounts against an
  // engagement that doesn't have one yet — the user already explicitly scheduled the
  // assessment to get here, so requiring a second "Start Assessment" click first is pure
  // friction. Guarded so it only ever fires once per mount, not on every poll/refetch;
  // `runAutoCreate` is also exposed as a manual retry if it fails.
  const autoCreateAttempted = useRef(false);
  const runAutoCreate = async () => {
    setAutoCreating(true);
    setAutoCreateError('');
    try {
      await createAssessment({ engagement_id: engagementId, assessment_template_code: DEFAULT_TEMPLATE_CODE }).unwrap();
    } catch (e) {
      setAutoCreateError((e as { data?: { message?: string } })?.data?.message || 'Could not start the assessment.');
    } finally {
      setAutoCreating(false);
    }
  };
  useEffect(() => {
    if (isLoading || assessment || autoCreateAttempted.current) return;
    autoCreateAttempted.current = true;
    void runAutoCreate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, assessment, engagementId]);

  const { data: notes = [] } = useListNotesByContextQuery(
    assessment ? { contextType: 'assessment', entityId: assessment.assessment_id } : { contextType: 'assessment', entityId: '' },
    { skip: !assessment },
  );

  // panelRef kept for scrollPanelIntoView — outside-click-to-close removed because panels can
  // hold in-progress data (voice recording, unsaved segment form) and portal-rendered pickers
  // (date, select) would close the panel mid-interaction. Close is explicit (toggle button / X).
  const panelRef = useRef<HTMLDivElement | null>(null);

  if (isLoading) {
    return <p className="text-xs text-ui-text3">Loading assessment…</p>;
  }
  if (!assessment) {
    return autoCreateError ? (
      <div className="space-y-1.5">
        <p className="text-xs text-danger-text">{autoCreateError}</p>
        <Button size="sm" variant="outline" disabled={autoCreating} onClick={() => void runAutoCreate()}>
          {autoCreating ? 'Retrying…' : 'Try again'}
        </Button>
      </div>
    ) : (
      <p className="text-xs text-ui-text3">Starting the assessment…</p>
    );
  }

  const scrollPanelIntoView = (el: HTMLDivElement | null) => {
    panelRef.current = el;
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleSaveTextNote = async () => {
    if (!noteText.trim()) return;
    setError('');
    try {
      await createNote({
        source: 'text',
        content: noteText.trim(),
        final_transcript: noteText.trim(),
        context: { context_type: 'assessment', entity_id: assessment.assessment_id },
        custom_tags: noteTag.trim() ? [noteTag.trim()] : undefined,
      }).unwrap();
      setNoteText('');
      setNoteTag('');
      setPanel(null);
    } catch (e) {
      setError((e as { data?: { message?: string } })?.data?.message || 'Could not save the note.');
    }
  };

  const handleUploadReport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    try {
      await uploadReport({ assessment_id: assessment.assessment_id, file }).unwrap();
      setPanel(null);
    } catch (err) {
      setError((err as { data?: { message?: string } })?.data?.message || 'Failed to upload the measurement report.');
    } finally {
      e.target.value = '';
    }
  };

  const handleUploadPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !propertyId) return;
    setError('');
    try {
      const uploaded = await uploadMediaFile((() => { const fd = new FormData(); fd.append('file', file); return fd; })()).unwrap();
      await attachMedia({
        propertyId,
        body: {
          engagement_id: engagementId,
          s3_url: uploaded.url,
          media_type: 'photo',
          filename: uploaded.filename,
          classifications: [photoTag],
        },
      }).unwrap();
      setPanel(null);
    } catch (err) {
      setError((err as { data?: { message?: string } })?.data?.message || 'Failed to upload the photo.');
    } finally {
      e.target.value = '';
    }
  };

  // Each of these used to `.unwrap()` with no try/catch, so a refused write rejected unhandled:
  // the popover simply stayed open and nothing on screen said why. They close only on success now,
  // and report the failure through the card's own error line.
  const handleRetagPhoto = async (mediaId: string, tag: string) => {
    if (!propertyId) return;
    setError('');
    try {
      await updateMedia({ mediaId, propertyId, body: { classifications: [tag] } }).unwrap();
    } catch (e) {
      setError((e as { data?: { message?: string } })?.data?.message || 'Could not re-tag that photo.');
    }
  };

  const handleDeletePhoto = async (mediaId: string) => {
    if (!propertyId) return;
    setError('');
    try {
      await deleteMedia({ mediaId, propertyId }).unwrap();
      setConfirmDeletePhotoId(null);
      setLightboxPhotoId(null);
    } catch (e) {
      setError((e as { data?: { message?: string } })?.data?.message || 'Could not delete that photo.');
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    setError('');
    try {
      await deleteNote(noteId).unwrap();
      setConfirmDeleteNoteId(null);
    } catch (e) {
      setError((e as { data?: { message?: string } })?.data?.message || 'Could not delete that note.');
    }
  };

  // Re-transcribes an existing recording that somehow ended up with no transcript (the live
  // recorder above always attaches one before saving, so this only matters for a note whose
  // in-browser STT came back empty) — fetches the SAME audio already stored in S3 and re-submits
  // it to the real multipart transcription fallback (notes/service.py::attach_audio, real OpenAI
  // call), rather than faking a "Transcribe" button with no backend behind it.
  const handleTranscribe = async (note: Note) => {
    if (!note.audio_url) return;
    setTranscribeErrorId(null);
    try {
      const res = await fetch(note.audio_url);
      if (!res.ok) throw new Error('download failed');
      const blob = await res.blob();
      const file = new File([blob], `${note.note_id}.webm`, { type: blob.type || 'audio/webm' });
      await uploadNoteAudio({ id: note.note_id, file }).unwrap();
    } catch {
      setTranscribeErrorId(note.note_id);
    }
  };

  const handleGenerateSummary = async () => {
    setSummaryError('');
    try {
      await generateSummary(assessment.assessment_id).unwrap();
    } catch (e) {
      setSummaryError((e as { data?: { message?: string } })?.data?.message || 'Could not generate a summary — try again.');
    }
  };

  const togglePanel = (p: Panel) => {
    setError('');
    setPanel((cur) => (cur === p ? null : p));
  };

  // Dashed-border quick-action buttons (Enter manually / Add photo / ...) — matches the reference
  // capture UI's action row styling.
  const actionBtnCls = (active: boolean) => `flex h-10 items-center justify-center gap-1.5 rounded-lg border border-dashed px-2 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-ui-card ${
    active ? 'border-accent bg-accent-fill text-on-accent' : 'border-ui-border bg-ui-card text-ui-text2 hover:bg-ui-hover hover:border-transparent btn-icon-hover'
  }`;

  const notePillCls = (active: boolean) => `rounded-full px-2.5 py-1 text-xs font-medium capitalize transition-colors ${
    active ? 'bg-accent-fill text-on-accent' : 'border border-ui-border bg-ui-card text-ui-text2 hover:bg-ui-hover'
  }`;


  // Editable Assessment Summary rows — 'measurement' fields (segment-consolidated) go through
  // the override endpoint; 'assessment' fields (existing_layers, roof_complexity, ...) already
  // had a direct write path (setAssessmentField). Both re-run validation server-side, so a save
  // that clears the last blocking field flips ready_for_requirement_generation without a
  // separate Regenerate click.
  // Reports the failure, then RETHROWS so EditableField can put the row back to the stored value.
  // Without the report, a rejected save was completely silent — the field kept showing the typed
  // number and the user only found out when Complete Assessment stayed blocked with no reason.
  const handleFieldSave = async (spec: FieldSpec, value: unknown) => {
    setError('');
    try {
      if (spec.kind === 'measurement') {
        await setMeasurementOverride({ assessment_id: assessment.assessment_id, field_code: spec.field_code, value }).unwrap();
      } else {
        await setAssessmentField({ assessment_id: assessment.assessment_id, field_code: spec.field_code, value, source_type: 'MANUAL' }).unwrap();
      }
    } catch (e) {
      setError(
        (e as { data?: { message?: string } })?.data?.message
        || `Could not save ${spec.field_name}. It has not been changed.`,
      );
      throw e;
    }
  };
  const handleFieldReset = async (field_code: string) => {
    setError('');
    try {
      await clearMeasurementOverride({ assessment_id: assessment.assessment_id, field_code }).unwrap();
    } catch (e) {
      setError((e as { data?: { message?: string } })?.data?.message || 'Could not reset that field to its computed value.');
    }
  };

  // Photos captured for THIS assessment specifically — propertyMedia is every photo ever taken
  // on the property, so scope down to this engagement (same context tag pattern the meeting/
  // notes queries above use) before it's meaningful to show or count here.
  const assessmentPhotos = propertyMedia.filter((m) => m.engagement_id === engagementId && m.media_type === 'photo');
  const filteredPhotos = photoFilter ? assessmentPhotos.filter((p) => p.classifications?.includes(photoFilter)) : assessmentPhotos;

  // One-line status for the collapsed header — cheap to derive from what's already fetched
  // rather than a separate summary endpoint; mirrors the same "what's the state" question the
  // Generate Summary readiness verdict answers in full once expanded.
  const segments = assessment.detailed_measurements_segments;
  const manualSegments = segments.filter((seg) => seg.source_type === 'MANUAL');
  const capturedCount = assessment.detailed_measurements_segments.length + (assessment.measurement_report ? 1 : 0) + notes.length + assessmentPhotos.length;
  const generation = assessment.summary_generation;
  const rawIsGenerating = !!generation && generation.status !== 'DONE' && generation.status !== 'FAILED';
  const isGenerating = rawIsGenerating;
  // Generate Summary REWRITES this assessment — it drops the segments a previous run applied and
  // re-applies a fresh set. Capturing into it mid-run races that: a note or plane added while the
  // pipeline is between those two steps is read by neither, so it silently never reaches the
  // summary. Locked for the duration rather than merged afterwards, because there is no honest
  // way to merge a value the reconciliation never saw.
  const captureLocked = isGenerating;
  const removeSegment = async (seg: Segment) => {
    setError('');
    try {
      await deleteSegment({ assessment_id: assessment.assessment_id, segment_code: seg.segment_code }).unwrap();
      if (editingSegment?.segment_code === seg.segment_code) { setEditingSegment(null); setPanel(null); }
    } catch (e) {
      setError((e as { data?: { message?: string } })?.data?.message || 'Could not remove that measurement.');
    }
  };
  const generationFailed = generation?.status === 'FAILED';
  // The generation stage takes over the one-line status — including while COLLAPSED — so a job
  // running in the background is visible without expanding the card, matching how the header
  // already surfaces "Ready to complete"/"Missing N fields" without requiring a click.
  const captureStatus = isGenerating
    ? generation!.stage_label
    : generationFailed
      ? t('assessments.summaryFailedShort', 'Summary generation failed — see below')
      : !assessment.generated_summary
        ? (capturedCount > 0 ? `${capturedCount} item${capturedCount === 1 ? '' : 's'} captured` : 'Not started yet')
        : assessment.validation_summary.ready_for_requirement_generation
          ? 'Ready to complete'
          : `Missing ${assessment.validation_summary.blocking_issue_count} required field${assessment.validation_summary.blocking_issue_count === 1 ? '' : 's'}`;

  const meetingTimeLabel = meeting?.start_time
    ? new Date(meeting.start_time).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : null;
  // Every task on the platform is ENGAGEMENT-scoped — 220 of 220 in dev, and nothing creates a
  // task with an assessment context — so this count cannot be narrowed to "assessment tasks"
  // however it is filtered. It was labelled "N tasks open" inside a card titled "Assessment
  // meeting & capture", which reads as the assessment's own tasks. Observed misattributing a
  // live "Prepare Proposal" task to the assessment once the engagement reached
  // assessment_completed.
  //
  // Naming the scope is the fix rather than removing the chip: the count is real and useful,
  // it was only the position that implied a narrower subject than the data supports.
  const openTaskCount = (tasks ?? []).filter((tk) => !OPEN_TASK_STATUSES_EXCLUDED.has(tk.status)).length;

  const captureSummary = [
    meetingTimeLabel,
    openTaskCount > 0
      ? t('tasks.openOnEngagement', '{{count}} engagement task open', { count: openTaskCount })
      : null,
    captureStatus,
  ].filter(Boolean).join(' · ');

  return (
    <div ref={rootRef} className={`scroll-mt-3 ${expanded ? 'space-y-0' : 'space-y-2'}`}>
      {/* Visible regardless of fold state (outside the collapsible toggle below) — a background
          job shouldn't need the card expanded to be noticed, the same reasoning the Quick Quote
          bar's own real-data-while-folded fix followed. */}
      {isGenerating && (
        <div className="flex items-center gap-2 rounded-lg bg-accent-fill px-3 py-2 text-on-accent">
          <span className="h-3.5 w-3.5 flex-shrink-0 animate-spin rounded-full border-2 border-white/35 border-t-white" />
          <span className="text-xs font-semibold">
            Assessment summary generation in progress — {generation!.stage_label}
          </span>
        </div>
      )}
      {!hideHeader && !expanded && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="group flex w-full items-center justify-between gap-2 rounded-xl border border-ui-border bg-ui-card px-3 py-2.5 text-left transition-colors hover:bg-ui-hover btn-icon-hover"
        >
          <div className="min-w-0">
            <p className="text-sm font-semibold text-ui-text">Assessment meeting & capture</p>
            <p className="mt-0.5 truncate text-xs text-ui-text3">{captureSummary}</p>
          </div>
          <span className="flex flex-shrink-0 items-center gap-1 text-xs font-medium text-accent-text group-hover:text-ui-text transition-colors">
            View
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </span>
        </button>
      )}

      {expanded && (
      <div className="rounded-xl border border-ui-border overflow-hidden">
        {!hideHeader && (
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="group flex w-full items-center justify-between gap-2 border-b border-ui-border bg-ui-card px-3 py-2.5 text-left transition-colors hover:bg-ui-hover"
          >
            <div className="min-w-0">
              <p className="text-sm font-semibold text-ui-text">Assessment meeting & capture</p>
              <p className="mt-0.5 truncate text-xs text-ui-text3">{captureSummary}</p>
            </div>
            <span className="flex flex-shrink-0 items-center gap-1 text-xs font-medium text-accent-text group-hover:text-ui-text transition-colors">
              Hide
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="rotate-180">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </span>
          </button>
        )}
        <div className="space-y-2 bg-ui-card px-3 py-2" onPointerDown={() => panel && setPanel(null)}>
      {meeting && <WorkspaceMeetingCard meeting={meeting} hideContextLink />}

      {error && <p className="text-xs text-danger-text">{error}</p>}

      {/* CAPTURED CONTENT — shown whether or not this card is editable. */}

      <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <p className="text-micro font-bold uppercase tracking-[0.06em] text-ui-text3">
              Manual measurements ({manualSegments.length})
            </p>
            {!readOnly && manualSegments.length > 0 && (
              <button type="button" disabled={captureLocked} onClick={() => { setEditingSegment(null); togglePanel('manual'); }} className="text-xs font-semibold text-accent-text hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline">+ Enter</button>
            )}
          </div>
          {!readOnly && panel === 'manual' && (
            <div ref={scrollPanelIntoView} onPointerDown={(e) => e.stopPropagation()}>
              <SegmentEntryForm
                key={editingSegment?.segment_code ?? 'new'}
                assessmentId={assessment.assessment_id}
                templateCode={assessment.assessment_template_code}
                templateVersion={assessment.assessment_template_version}
                editSegment={editingSegment}
                onDone={() => { setEditingSegment(null); setPanel(null); }}
              />
            </div>
          )}
          {manualSegments.length === 0 && !readOnly && panel !== 'manual' && (
            <button type="button" disabled={captureLocked} onClick={() => { setEditingSegment(null); togglePanel('manual'); }} className={`w-full ${actionBtnCls(false)}`}>
              <IcoRuler /> Enter measurements
            </button>
          )}
          {manualSegments.length > 0 && <div className="divide-y divide-ui-border rounded-lg border border-ui-border bg-ui-card">
            {manualSegments.map((seg) => (
              <div key={seg.segment_code} className="flex items-start justify-between gap-2 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <ManualSegmentList segments={[seg]} />
                </div>
                {!readOnly && (segmentMenuOpen === seg.segment_code ? (
                  /* Same shape as the note rows: ⋯ swaps in place for the actions rather than
                     opening a floating menu. */
                  <div className="flex flex-shrink-0 gap-1">
                    <button
                      type="button"
                      onClick={() => {
                        setError('');
                        setEditingSegment(seg);
                        setPanel('manual');
                        setSegmentMenuOpen(null);
                      }}
                      className="rounded border border-ui-border px-1.5 py-0.5 text-xs text-ui-text2"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      disabled={isRemovingSegment}
                      onClick={() => { setSegmentMenuOpen(null); void removeSegment(seg); }}
                      className="rounded bg-danger px-1.5 py-0.5 text-xs font-semibold text-white disabled:opacity-50"
                    >
                      Remove
                    </button>
                    <button
                      type="button"
                      onClick={() => setSegmentMenuOpen(null)}
                      className="rounded border border-ui-border px-1.5 py-0.5 text-xs text-ui-text2"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    disabled={captureLocked}
                    title="Edit or remove"
                    onClick={() => setSegmentMenuOpen(seg.segment_code)}
                    className="flex-shrink-0 text-ui-text3 hover:text-ui-text disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    ⋯
                  </button>
                ))}
              </div>
            ))}
          </div>}
        </div>

      <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <p className="text-micro font-bold uppercase tracking-[0.06em] text-ui-text3">Measurement file ({assessment.measurement_report ? 1 : 0})</p>
            {!readOnly && assessment.measurement_report && (
              <button type="button" disabled={captureLocked} onClick={() => togglePanel('upload')} className="text-xs font-semibold text-accent-text hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline">
                + Replace
              </button>
            )}
          </div>
          {!readOnly && panel === 'upload' && (
            <div ref={scrollPanelIntoView} className="space-y-1.5 rounded-lg border border-ui-border bg-ui-subtle p-2" onPointerDown={(e) => e.stopPropagation()}>
              <p className="text-xs text-ui-text3">
                {assessment.measurement_report
                  ? 'Replacing the report — pick a new PDF file.'
                  : 'The report is stored as-is and combined with your photos, notes and measurements when you hit Generate Summary.'}
              </p>
              <label className="inline-block">
                <input type="file" accept="application/pdf" className="hidden" onChange={(e) => void handleUploadReport(e)} disabled={isUploading} />
                <span className="inline-flex items-center gap-1.5 h-7 px-3 rounded-lg text-xs font-medium border border-ui-border bg-ui-card text-ui-text hover:bg-ui-hover cursor-pointer">
                  <IcoUpload />
                  {isUploading ? 'Uploading…' : assessment.measurement_report ? 'Replace Report (PDF)' : 'Upload Report (PDF)'}
                </span>
              </label>
            </div>
          )}
          {!assessment.measurement_report && !readOnly && panel !== 'upload' && (
            <button type="button" disabled={captureLocked} onClick={() => togglePanel('upload')} className={`w-full ${actionBtnCls(false)}`}>
              <IcoUpload /> Upload report
            </button>
          )}
          {assessment.measurement_report && (
            <>
            <button
              type="button"
              onClick={() => setShowReportPreview(true)}
              className="flex w-full items-center justify-between gap-3 rounded-lg border border-ui-border bg-ui-card px-3 py-2 text-left hover:bg-ui-hover"
            >
              <div className="flex min-w-0 items-center gap-2">
                <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded bg-ui-subtle text-xs font-bold text-ui-text3">PDF</span>
                <div className="min-w-0">
                  <p className="truncate text-sm text-ui-text">{assessment.measurement_report.filename || 'Measurement report'}</p>
                  <p className="text-xs capitalize text-ui-text3">
                    {assessment.measurement_report.status}
                    {assessment.measurement_report.uploaded_on ? ` · ${new Date(assessment.measurement_report.uploaded_on).toLocaleDateString()}` : ''}
                  </p>
                </div>
              </div>
              <span className="flex-shrink-0 text-xs font-semibold text-accent-text">Preview →</span>
            </button>
            {assessment.measurement_report.error && <p className="text-xs text-danger-text">{assessment.measurement_report.error}</p>}
            </>
          )}
        </div>

      <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <p className="text-micro font-bold uppercase tracking-[0.06em] text-ui-text3">Photos ({assessmentPhotos.length})</p>
            {!readOnly && assessmentPhotos.length > 0 && (
              <button type="button" disabled={captureLocked} onClick={() => togglePanel('photo')} className="text-xs font-semibold text-accent-text hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline">+ Add photo</button>
            )}
          </div>
          {!readOnly && panel === 'photo' && (
            <div ref={scrollPanelIntoView} className="space-y-1 rounded-lg border border-ui-border bg-ui-subtle p-2" onPointerDown={(e) => e.stopPropagation()}>
              <p className="text-xs font-medium text-ui-text3">Tag this photo</p>
              <div className="flex flex-wrap gap-1.5">
                {PHOTO_TAGS.map((tag) => (
                  <button key={tag} type="button" onClick={() => setPhotoTag(tag)}
                    className={`rounded-full px-2.5 py-1 text-xs font-medium capitalize transition-colors ${photoTag === tag ? 'bg-accent-fill text-on-accent' : 'border border-ui-border bg-ui-card text-ui-text2 hover:bg-ui-hover'}`}
                  >{tag}</button>
                ))}
              </div>
              <div className="flex gap-2">
                <label className="flex-1">
                  <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => void handleUploadPhoto(e)} disabled={isUploadingPhoto || !propertyId} />
                  <span className="flex h-7 items-center justify-center gap-1.5 rounded-lg border border-ui-border bg-ui-card px-3 text-xs font-medium text-ui-text hover:bg-ui-hover cursor-pointer">
                    <IcoCamera /> {isUploadingPhoto ? 'Uploading…' : 'Take photo'}
                  </span>
                </label>
                <label className="flex-1">
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => void handleUploadPhoto(e)} disabled={isUploadingPhoto || !propertyId} />
                  <span className="flex h-7 items-center justify-center gap-1.5 rounded-lg border border-ui-border bg-ui-card px-3 text-xs font-medium text-ui-text hover:bg-ui-hover cursor-pointer">
                    <IcoFolderOpen /> {isUploadingPhoto ? 'Uploading…' : 'Browse & upload'}
                  </span>
                </label>
              </div>
              {!propertyId && <p className="text-xs text-danger-text">No property linked — can&apos;t attach a photo.</p>}
            </div>
          )}
          {assessmentPhotos.length === 0 && !readOnly && panel !== 'photo' && (
            <button type="button" disabled={captureLocked} onClick={() => togglePanel('photo')} className={`w-full ${actionBtnCls(false)}`}>
              <span aria-hidden>📷</span> Add photo
            </button>
          )}
          {assessmentPhotos.length > 0 && (
            <>
              <div className="flex flex-wrap gap-1.5">
                <button type="button" onClick={() => setPhotoFilter(null)} className={notePillCls(photoFilter === null)}>
                  All {assessmentPhotos.length}
                </button>
                {PHOTO_TAGS.map((tag) => {
                  const count = assessmentPhotos.filter((p) => p.classifications?.includes(tag)).length;
                  if (count === 0) return null;
                  return (
                    <button key={tag} type="button" onClick={() => setPhotoFilter(tag)} className={notePillCls(photoFilter === tag)}>
                      {tag} {count}
                    </button>
                  );
                })}
              </div>
              <div className="flex snap-x snap-mandatory gap-2 overflow-x-auto pb-1">
                {filteredPhotos.map((p) => (
                  <div key={p.media_id} className="relative flex-shrink-0">
                    <div className="relative snap-start overflow-hidden rounded-[9px] border border-ui-border bg-ui-subtle h-24 w-24">
                      <button
                        type="button"
                        onClick={() => setLightboxPhotoId(p.media_id)}
                        className="block h-full w-full"
                      >
                        <img src={p.s3_url} alt={p.classifications?.[0] ?? 'photo'} className="h-full w-full object-cover" loading="lazy" />
                      </button>
                      {p.classifications?.[0] && (
                        <span className="pointer-events-none absolute bottom-1 left-1 rounded-[5px] bg-black/70 px-1.5 py-0.5 text-xs font-semibold capitalize leading-tight text-white">
                          {p.classifications[0]}
                        </span>
                      )}
                      {!readOnly && (
                        <button
                          type="button"
                          title="Delete photo"
                          onClick={(e) => { e.stopPropagation(); setConfirmDeletePhotoId(p.media_id); }}
                          className="absolute right-1 top-1 flex h-[18px] w-[18px] items-center justify-center rounded-[5px] bg-black/70 text-xs leading-none text-white hover:bg-black/85"
                        >
                          ×
                        </button>
                      )}
                    </div>
                    {confirmDeletePhotoId === p.media_id && (
                      <div className="absolute top-full left-0 z-10 mt-1 w-40 rounded-lg border border-danger/30 bg-danger/10 p-2 shadow-lg">
                        <p className="text-xs font-medium text-danger-text">Delete this photo?</p>
                        <div className="mt-1.5 flex gap-1.5">
                          <button type="button" onClick={() => void handleDeletePhoto(p.media_id)} className="flex-1 rounded-lg bg-danger py-1 text-xs font-semibold text-white">Delete</button>
                          <button type="button" onClick={() => setConfirmDeletePhotoId(null)} className="flex-1 rounded-lg border border-ui-border py-1 text-xs font-medium text-ui-text2">Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-xs text-ui-text3">Scroll for more · tap a photo to preview, × to delete.</p>
            </>
          )}
        </div>

      {(() => {
        const voiceNotes = notes.filter((n) => n.source === 'voice');
        return (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <p className="text-micro font-bold uppercase tracking-[0.06em] text-ui-text3">Voice notes ({voiceNotes.length})</p>
              {!readOnly && voiceNotes.length > 0 && (
                <button type="button" onClick={() => togglePanel('voice')} className="text-xs font-semibold text-accent-text hover:underline">+ Record</button>
              )}
            </div>
            {!readOnly && panel === 'voice' && (
              <div ref={scrollPanelIntoView} className="space-y-1 rounded-lg border border-accent/30 bg-accent-soft/30 dark:bg-accent/25 p-2" onPointerDown={(e) => e.stopPropagation()}>
                <p className="text-xs font-medium text-ui-text3">Tag</p>
                <div className="flex flex-wrap gap-1">
                  {NOTE_TAGS.map((tag) => (
                    <button key={tag} type="button" onClick={() => setVoiceTag(tag)} className={notePillCls(voiceTag === tag)}>
                      {tag}
                    </button>
                  ))}
                </div>
                <VoiceNoteRecorder
                  contextType="assessment"
                  entityId={assessment.assessment_id}
                  onCancel={() => { setVoiceTag(''); setPanel(null); }}
                  onSaved={(note) => {
                    if (voiceTag.trim()) {
                      void updateNote({ id: note.note_id, body: { custom_tags: [voiceTag.trim()] } });
                    }
                    setVoiceTag('');
                    setPanel(null);
                  }}
                />
              </div>
            )}
            {voiceNotes.length === 0 && !readOnly && panel !== 'voice' && (
              <button type="button" onClick={() => togglePanel('voice')} className={`w-full ${actionBtnCls(false)}`}>
                <IcoMic /> Record voice note
              </button>
            )}
            {voiceNotes.map((n) => {
              const transcript = n.clean_transcript || n.final_transcript;
              const tag = n.custom_tags?.[0];
              return (
                <div key={n.note_id} className="rounded-[9px] border border-ui-border bg-ui-card px-3 py-2.5">
                  <div className="flex items-start justify-between gap-2.5">
                    <div className="min-w-0">
                      {/* Tag and time only. The reference also showed a duration and the
                          recorder's name; a note carries neither — just owner_user_id and
                          created_on — and the audio element below reports the real duration
                          once it loads. */}
                      {tag && <span className="text-xs font-semibold capitalize text-ui-text">{tag}</span>}
                      <p className="mt-0.5 truncate text-xs text-ui-text3">
                        {new Date(n.created_on).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    {!readOnly && (confirmDeleteNoteId === n.note_id ? (
                      <div className="flex flex-shrink-0 gap-1">
                        <button type="button" onClick={() => void handleDeleteNote(n.note_id)} className="rounded bg-danger px-1.5 py-0.5 text-xs font-semibold text-white">Delete</button>
                        <button type="button" onClick={() => setConfirmDeleteNoteId(null)} className="rounded border border-ui-border px-1.5 py-0.5 text-xs text-ui-text2">Cancel</button>
                      </div>
                    ) : (
                      <button type="button" onClick={() => setConfirmDeleteNoteId(n.note_id)} className="flex-shrink-0 text-ui-text3 hover:text-ui-text" title="Delete note">⋯</button>
                    ))}
                  </div>
                  {n.audio_url && (
                    <audio controls preload="none" src={n.audio_url} className="mt-1.5 h-8 w-full">
                      Your browser does not support audio playback.
                    </audio>
                  )}
                  {transcript ? (
                    <p className="mt-1 text-xs italic text-ui-text2">&quot;{transcript}&quot;</p>
                  ) : (
                    <div className="mt-1.5">
                      <Button size="sm" variant="outline" disabled={isTranscribing} onClick={() => void handleTranscribe(n)}>
                        {isTranscribing ? 'Transcribing…' : 'Transcribe'}
                      </Button>
                      {transcribeErrorId === n.note_id && <p className="mt-1 text-xs text-danger-text">Could not transcribe — try again.</p>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })()}

      {(() => {
        const textNotes = notes.filter((n) => n.source !== 'voice');
        return (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <p className="text-micro font-bold uppercase tracking-[0.06em] text-ui-text3">Notes ({textNotes.length})</p>
              {!readOnly && textNotes.length > 0 && (
                <button type="button" disabled={captureLocked} onClick={() => togglePanel('text')} className="text-xs font-semibold text-accent-text hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline">+ Add note</button>
              )}
            </div>
            {!readOnly && panel === 'text' && (
              <div ref={scrollPanelIntoView} className="space-y-1 rounded-lg border border-accent/30 bg-accent-soft/30 dark:bg-accent/25 p-2" onPointerDown={(e) => e.stopPropagation()}>
                <textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Type a note about the assessment…"
                  rows={2}
                  autoFocus
                  className="w-full rounded-md border border-ui-border bg-ui-input px-2 py-1 !text-xs text-ui-text placeholder:text-ui-text3 resize-none focus:outline-none focus:border-ui-ring"
                />
                <p className="text-xs font-medium text-ui-text3">Tag</p>
                <div className="flex flex-wrap gap-1">
                  {NOTE_TAGS.map((tag) => (
                    <button key={tag} type="button" onClick={() => setNoteTag(tag)} className={notePillCls(noteTag === tag)}>
                      {tag}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" disabled={!noteText.trim() || isSavingNote} onClick={() => void handleSaveTextNote()} className="h-6 md:h-7 min-h-0 flex-1 justify-center text-xs">
                    {isSavingNote ? 'Saving…' : 'Save note'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setNoteText(''); setNoteTag(''); setPanel(null); }} className="h-6 md:h-7 min-h-0 text-xs">Cancel</Button>
                </div>
              </div>
            )}
            {textNotes.length === 0 && !readOnly && panel !== 'text' && (
              <button type="button" disabled={captureLocked} onClick={() => togglePanel('text')} className={`w-full ${actionBtnCls(false)}`}>
                <IcoFileText /> Add note
              </button>
            )}
            {textNotes.map((n) => {
              const tag = n.custom_tags?.[0];
              return (
                <div key={n.note_id} className="rounded-[9px] border border-ui-border border-l-[3px] border-l-accent-soft bg-ui-card px-3 py-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex min-w-0 items-baseline gap-2">
                      {tag && <span className="text-xs font-semibold capitalize text-accent-text">{tag}</span>}
                      <span className="truncate text-xs text-ui-text3">
                        {new Date(n.created_on).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    {!readOnly && (confirmDeleteNoteId === n.note_id ? (
                      <div className="flex flex-shrink-0 gap-1">
                        <button type="button" onClick={() => void handleDeleteNote(n.note_id)} className="rounded bg-danger px-1.5 py-0.5 text-xs font-semibold text-white">Delete</button>
                        <button type="button" onClick={() => setConfirmDeleteNoteId(null)} className="rounded border border-ui-border px-1.5 py-0.5 text-xs text-ui-text2">Cancel</button>
                      </div>
                    ) : (
                      <button type="button" onClick={() => setConfirmDeleteNoteId(n.note_id)} className="flex-shrink-0 text-ui-text3 hover:text-ui-text" title="Delete note">⋯</button>
                    ))}
                  </div>
                  <p className="mt-1 text-xs leading-relaxed text-ui-text">{n.clean_transcript || n.final_transcript || n.summary || '(no content)'}</p>
                </div>
              );
            })}
          </div>
        );
      })()}


      {/* Generate Summary — the single compile step. Reads everything captured above (report
          PDF, photos, voice/text notes, manual segments), reconciles it via the roof measurement
          pipeline, and writes the result onto the assessment, which re-runs its validation — so
          what's still missing (or that nothing is) is the assessment's OWN validation state
          below, not just a report about the compile. Runs as a background job server-side (real
          LLM calls, up to a couple of minutes) — this click only starts it; the box below tracks
          it through the SAME real stages the backend actually reaches, via the 5s poll already
          running above, not a simulated timer. */}
      <div className="border-t border-ui-border pt-3">
        {!readOnly && !isGenerating && !generationFailed && (
          <Button
            className="w-full justify-center"
            disabled={isGeneratingSummary}
            onClick={() => void handleGenerateSummary()}
          >
            {isGeneratingSummary ? 'Starting…' : 'Generate Measurement'}
          </Button>
        )}
        {!readOnly && summaryError && <p className="mt-1.5 text-xs text-danger-text">{summaryError}</p>}

        {!readOnly && isGenerating && (
          <div className="rounded-xl border border-accent/30 bg-accent-soft px-3.5 py-3.5">
            <div className="flex items-center gap-2.5">
              <span className="h-4 w-4 flex-shrink-0 animate-spin rounded-full border-2 border-accent/25 border-t-accent" />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold text-accent-text">Assessment summary generation in progress</p>
                <p className="mt-0.5 text-xs text-accent-text/80">{generation!.stage_label}</p>
              </div>
            </div>
            <div className="mt-2.5 h-1 overflow-hidden rounded-full bg-accent/15">
              <div
                className="h-full rounded-full bg-accent transition-all duration-700"
                style={{ width: `${GENERATION_STAGE_PROGRESS[generation!.status] ?? 5}%` }}
              />
            </div>
            <p className="mt-2 text-xs leading-relaxed text-ui-text3">
              This runs in the background — you can keep working. This card updates on its own
              once the summary is ready.
            </p>
          </div>
        )}

        {!readOnly && generationFailed && (
          <div className="rounded-xl border border-danger/30 bg-danger/10 px-3.5 py-3">
            <p className="text-xs font-semibold text-danger-text">
              {t('assessments.summaryFailed', 'Summary generation failed')}
            </p>
            {/* The backend's own error, always. The other branch here used to invent a "hasn't
                reported progress in a while" message for a run that was usually still working —
                a guess presented to the user as a diagnosis. */}
            <p className="mt-1 text-xs text-danger-text/85">{generation?.error}</p>
            <Button size="sm" className="mt-2" disabled={isGeneratingSummary} onClick={() => void handleGenerateSummary()}>
              {isGeneratingSummary ? 'Starting…' : 'Try again'}
            </Button>
          </div>
        )}

        {assessment.generated_summary && (
          <div className="mt-3 space-y-3">
            {(() => {
              const gs = assessment.generated_summary!;
              const roof = gs.data?.roof ?? {};
              const sections = gs.data?.sections ?? [];
              const damage = gs.data?.damage ?? [];
              const rec = gs.recommendations;
              const hasRecommendation = rec && Object.values(rec).some((v) => !!v);
              const blocking = assessment.validation_messages.filter((m) => m.severity === 'BLOCK');
              const stillWarned = assessment.validation_messages.filter((m) => m.severity === 'WARNING');
              const ready = assessment.validation_summary.ready_for_requirement_generation;

              // Every MANDATORY field on the tenant's own template — measurement (segment-
              // consolidated) and assessment (existing_layers/roof_complexity/...) alike — shown
              // as its own editable row whether it's already filled or not, so nothing mandatory
              // is only visible as a read-only error text somewhere else.
              const mandatoryFields: FieldSpec[] = [
                ...(template?.measurement_fields ?? [])
                  .filter((f) => f.requirement_level === 'MANDATORY')
                  .map((f) => toFieldSpec(f, 'measurement')),
                ...(template?.assessment_fields ?? [])
                  .filter((f) => f.requirement_level === 'MANDATORY')
                  .map((f) => toFieldSpec(f, 'assessment')),
              ];
              const mvByCode = new Map(assessment.measurement_values.map((v) => [v.field_code, v]));
              const avByCode = new Map(assessment.assessment_values.map((v) => [v.field_code, v]));
              const blockMsgByCode = new Map(
                assessment.validation_messages
                  .filter((m) => m.severity === 'BLOCK' && m.field_code)
                  .map((m) => [m.field_code as string, m.message]),
              );

              return (
                <>
                  {/* Readiness verdict — one box; blocking field list + any pipeline warnings included inline. */}
                  <div className={`rounded-xl border p-3 space-y-1.5 ${ready ? 'border-success/30 bg-success/10' : 'border-warning/30 bg-warning/10'}`}>
                    {ready ? (
                      <p className="text-sm font-semibold text-success-text">✓ Everything required is captured</p>
                    ) : (
                      <p className="text-sm font-semibold text-warning-text">
                        Still missing {blocking.length} required {blocking.length === 1 ? 'field' : 'fields'}
                      </p>
                    )}
                    {blocking.length > 0 && (
                      <ul className="space-y-0.5">
                        {blocking.map((m, i) => (
                          <li key={i} className="text-xs text-warning-text/80">• {m.message}</li>
                        ))}
                      </ul>
                    )}
                    {(gs.warnings?.length > 0 || stillWarned.length > 0) && (
                      <ul className={`space-y-0.5 ${blocking.length > 0 ? 'border-t border-warning/30 pt-1.5' : ''}`}>
                        {gs.warnings?.map((w, i) => (
                          <li key={`gsw-${i}`} className={`text-xs ${ready ? 'text-success-text/70' : 'text-warning-text/60'}`}>• {w}</li>
                        ))}
                        {stillWarned.map((m, i) => (
                          <li key={`tmw-${i}`} className={`text-xs ${ready ? 'text-success-text/70' : 'text-warning-text/60'}`}>• {m.message}</li>
                        ))}
                      </ul>
                    )}
                    {!ready && (
                      <p className="text-xs text-warning-text/50">Fill in the fields below, or add a document/note and regenerate.</p>
                    )}
                  </div>

                  {(() => {
                    // Every row below is a REAL template measurement_field, sourced from
                    // assessment.measurement_values (not the gs.data.roof pipeline snapshot) so
                    // it reflects the current value live — including a manual override — and is
                    // directly editable via the same EditableField the Required fields section
                    // (now inside this same card) uses. Only rendered when the template actually
                    // defines that field_code (never assume a fixed field list) and it either has
                    // a value or is present on the template at all (measurement_fields not on this template
                    // simply produce no row).
                    const measurementSpecs = new Map(
                      (template?.measurement_fields ?? []).map((f) => [f.field_code, toFieldSpec(f, 'measurement')]),
                    );
                    const fmtFtIn = (n: number) => {
                      const totalIn = Math.round(n * 12);
                      const ft = Math.floor(totalIn / 12);
                      const inch = totalIn % 12;
                      return `${ft}ft ${inch}in`;
                    };
                    // Read-only by default (compact — only fields that actually have a value);
                    // Edit mode swaps every row in these groups for an EditableField AND shows
                    // every field the template defines even with no value yet, so a field that
                    // had nothing to show before can now be added, not just corrected.
                    const summaryRow = (code: string) => {
                      const spec = measurementSpecs.get(code);
                      if (!spec) return null;
                      const val = mvByCode.get(code)?.value ?? null;
                      if (!editingSummary) {
                        if (val == null) return null;
                        const display = typeof val === 'number' ? fmtFtIn(val) : String(val);
                        return (
                          <div key={code} className="flex items-center justify-between py-1">
                            <span className="text-xs text-accent-text">{spec.field_name}</span>
                            <span className="text-xs font-bold text-accent-text">{display}{spec.uom ? ` ${spec.uom}` : ''}</span>
                          </div>
                        );
                      }
                      return (
                        <EditableField
                          key={code}
                          spec={spec}
                          value={val}
                          blockedMessage={blockMsgByCode.get(code)}
                          overridden={code in assessment.measurement_overrides}
                          onSave={(value) => handleFieldSave(spec, value)}
                          onReset={() => handleFieldReset(code)}
                          disabled={readOnly}
                        />
                      );
                    };

                    const linearCodes = ['eave_length_lf', 'valley_length_lf', 'hip_length_lf', 'ridge_length_lf', 'rake_length_lf'];
                    const flashingCodes = [
                      'wall_flashing_length_lf', 'step_flashing_length_lf', 'transition_length_lf',
                      'parapet_wall_length_lf', 'unspecified_length_lf',
                    ];
                    const groupHasContent = (codes: string[]) => (
                      editingSummary
                        ? codes.some((c) => measurementSpecs.has(c))
                        : codes.some((c) => mvByCode.get(c)?.value != null)
                    );
                    const numericOf = (code: string): number | null => {
                      const v = mvByCode.get(code)?.value;
                      return typeof v === 'number' ? v : null;
                    };
                    const hipVal = numericOf('hip_length_lf');
                    const ridgeVal = numericOf('ridge_length_lf');
                    const eaveVal = numericOf('eave_length_lf');
                    const rakeVal = numericOf('rake_length_lf');
                    // COMBINED — trivial sums of fields already above (read-only; the reconciliation
                    // pipeline itself never does arithmetic, and neither field alone is directly
                    // editable as a "combined" quantity).
                    const combinedRows = [
                      hipVal != null && ridgeVal != null ? { key: 'hips_ridges', label: 'Hips + ridges', raw: hipVal + ridgeVal } : null,
                      eaveVal != null && rakeVal != null ? { key: 'eaves_rakes', label: 'Eaves + rakes', raw: eaveVal + rakeVal } : null,
                    ].filter((r): r is { key: string; label: string; raw: number } => !!r);

                    // MEASUREMENTS — AREAS: total/facets/pitch are real roof.* fields. Pitched vs.
                    // flat area isn't a field the pipeline reports (no is_flat flag either) — the
                    // one non-fabricated way to tell them apart is the "0/12" pitch notation, a
                    // standard roofing convention for a flat section, applied to `sections` (each
                    // {area_sqft, pitch}). Only rendered when sections actually carry pitch data —
                    // otherwise this would just be a guess dressed up as a number.
                    const sectionRows = sections.filter(
                      (s): s is { area_sqft: number; pitch: string } =>
                        typeof s.area_sqft === 'number' && typeof s.pitch === 'string',
                    );
                    const isFlatPitch = (p: string) => /^0[:/]\s*12$/.test(p.trim());
                    const flatArea = sectionRows.filter((s) => isFlatPitch(s.pitch)).reduce((sum, s) => sum + s.area_sqft, 0);
                    const pitchedArea = sectionRows.filter((s) => !isFlatPitch(s.pitch)).reduce((sum, s) => sum + s.area_sqft, 0);
                    const hasAreaSplit = sectionRows.length > 0 && (flatArea + pitchedArea) > 0;

                    // PITCH BREAKDOWN table — area per distinct pitch, derived the same way (grouping
                    // `sections` by pitch); squares is the standard roofing unit, 1 square = 100 sqft.
                    const pitchBreakdown = Object.values(
                      sectionRows.reduce<Record<string, { pitch: string; area: number }>>((acc, s) => {
                        acc[s.pitch] = { pitch: s.pitch, area: (acc[s.pitch]?.area ?? 0) + s.area_sqft };
                        return acc;
                      }, {}),
                    ).sort((a, b) => b.area - a.area);

                    // Source-count chips — same tallies already used for the collapsed header
                    // summary (capturedCount), split out here by type instead of combined.
                    const voiceNoteCount = notes.filter((n) => n.source === 'voice').length;
                    const textNoteCount = notes.length - voiceNoteCount;
                    const sourceChips = [
                      assessmentPhotos.length > 0 && `${assessmentPhotos.length} photo${assessmentPhotos.length === 1 ? '' : 's'}`,
                      voiceNoteCount > 0 && `${voiceNoteCount} voice note${voiceNoteCount === 1 ? '' : 's'}`,
                      textNoteCount > 0 && `${textNoteCount} note${textNoteCount === 1 ? '' : 's'}`,
                      assessment.measurement_report && `1 document`,
                    ].filter(Boolean) as string[];

                    return (
                    <div className="rounded-[9px] border border-accent-soft bg-accent-soft/30 dark:bg-accent/25 px-3.5 py-3.5">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-xs font-bold text-accent-text">✓ Assessment summary</p>
                        {!readOnly && (
                          <button
                            type="button"
                            disabled={isGeneratingSummary || isGenerating}
                            onClick={() => void handleGenerateSummary()}
                            className="text-xs font-semibold text-accent-text hover:underline disabled:opacity-50"
                          >
                            {isGeneratingSummary || isGenerating ? 'Regenerating…' : 'Regenerate'}
                          </button>
                        )}
                      </div>
                      <p className="mt-0.5 text-xs text-ui-text3">
                        {readOnly
                          ? 'Generated from captured measurements, photos and notes.'
                          : 'Values are editable — empty fields can be added too.'}
                      </p>

                      {/* Required fields — every MANDATORY template field (measurement AND
                          assessment), always shown filled or not, always editable regardless of
                          the Edit/Done toggle above (this is the actionable checklist, not just
                          descriptive summary data). */}
                      {mandatoryFields.length > 0 && (
                        <div className="mt-2 border-t border-ui-border pt-2">
                          <p className="text-micro font-bold uppercase tracking-[0.06em] text-accent-text">Required fields</p>
                          <div className="mt-1 divide-y divide-accent-soft/70">
                            {mandatoryFields.map((spec) => {
                              const stored = spec.kind === 'measurement' ? mvByCode.get(spec.field_code) : avByCode.get(spec.field_code);
                              return (
                                <EditableField
                                  key={spec.field_code}
                                  spec={spec}
                                  value={stored?.value ?? null}
                                  blockedMessage={blockMsgByCode.get(spec.field_code)}
                                  overridden={spec.kind === 'measurement' && spec.field_code in assessment.measurement_overrides}
                                  onSave={(value) => handleFieldSave(spec, value)}
                                  onReset={spec.kind === 'measurement' ? () => handleFieldReset(spec.field_code) : undefined}
                                  disabled={readOnly}
                                />
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* MEASUREMENTS — AREAS. roof_area_sf/roof_area_sq are MANDATORY and already
                          editable in Required fields above, so they're not repeated here (this
                          section is everything ELSE about area). Pitched/flat area split and
                          facets have no template field_code of their own, so stay read-only
                          display pulled from the pipeline result even in Edit mode. */}
                      <div id="assessment-section-measurements" className="mt-2 border-t border-ui-border pt-2">
                        <p className="text-micro font-bold uppercase tracking-[0.06em] text-accent-text">Measurements — areas</p>
                        <div className="mt-1 divide-y divide-accent-soft/70">
                          {hasAreaSplit && (
                            <>
                              <div className="flex items-center justify-between py-1">
                                <span className="text-xs text-accent-text">Total pitched area</span>
                                <span className="text-xs font-bold text-accent-text">{Math.round(pitchedArea)} sqft</span>
                              </div>
                              <div className="flex items-center justify-between py-1">
                                <span className="text-xs text-accent-text">Total flat area</span>
                                <span className="text-xs font-bold text-accent-text">{Math.round(flatArea)} sqft</span>
                              </div>
                            </>
                          )}
                          {roof.facets != null && (
                            <div className="flex items-center justify-between py-1">
                              <span className="text-xs text-accent-text">Total roof facets</span>
                              <span className="text-xs font-bold text-accent-text">{roof.facets} facets</span>
                            </div>
                          )}
                          {summaryRow('primary_roof_pitch')}
                        </div>
                      </div>

                      {groupHasContent(linearCodes) && (
                        <div className="mt-2 border-t border-ui-border pt-2">
                          <p className="text-micro font-bold uppercase tracking-[0.06em] text-accent-text">Linear measurements</p>
                          <div className="mt-1 divide-y divide-accent-soft/70">{linearCodes.map(summaryRow)}</div>
                        </div>
                      )}

                      {groupHasContent(flashingCodes) && (
                        <div className="mt-2 border-t border-ui-border pt-2">
                          <p className="text-micro font-bold uppercase tracking-[0.06em] text-accent-text">Flashing & transitions</p>
                          <div className="mt-1 divide-y divide-accent-soft/70">{flashingCodes.map(summaryRow)}</div>
                        </div>
                      )}

                      {combinedRows.length > 0 && (
                        <div className="mt-2 border-t border-ui-border pt-2">
                          <p className="text-micro font-bold uppercase tracking-[0.06em] text-accent-text">Combined</p>
                          <div className="mt-1 divide-y divide-accent-soft/70">
                            {combinedRows.map((r) => (
                              <div key={r.key} className="flex items-center justify-between py-1">
                                <span className="text-xs text-accent-text">{r.label}</span>
                                <span className="text-xs font-bold text-accent-text">{fmtFtIn(r.raw)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {roof.material && (
                        <div className="mt-2 flex items-center justify-between border-t border-ui-border pt-2">
                          <span className="text-xs text-accent-text">Material</span>
                          <span className="text-xs font-bold text-accent-text">{roof.material}</span>
                        </div>
                      )}

                      {pitchBreakdown.length > 0 && (
                        <div className="mt-2 border-t border-ui-border pt-2">
                          <p className="text-micro font-bold uppercase tracking-[0.06em] text-accent-text">Pitch breakdown</p>
                          <div className="mt-1 grid grid-cols-3 gap-1 text-xs font-medium text-ui-text3">
                            <span>Pitch</span>
                            <span className="text-right">Area (sqft)</span>
                            <span className="text-right">Squares</span>
                          </div>
                          <div className="divide-y divide-ui-border">
                            {pitchBreakdown.map((p) => (
                              <div key={p.pitch} className="grid grid-cols-3 gap-1 py-1 text-xs">
                                <span className="text-ui-text2">{p.pitch}</span>
                                <span className="text-right font-medium text-ui-text">{Math.round(p.area)}</span>
                                <span className="text-right font-medium text-ui-text">{(p.area / 100).toFixed(2)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {damage.length > 0 && (
                        <div className="mt-2 space-y-0.5 border-t border-ui-border pt-2">
                          {damage.map((d, i) => (
                            <p key={i} className="text-xs text-warning-text">⚠ {d.type?.replace(/_/g, ' ')}{d.severity && ` — ${d.severity}`}{d.location && ` (${d.location})`}</p>
                          ))}
                        </div>
                      )}

                      {sourceChips.length > 0 && (
                        <div className="mt-2.5 flex flex-wrap gap-1.5 border-t border-accent-soft pt-2.5">
                          {sourceChips.map((c) => (
                            <span key={c} className="rounded-md bg-ui-card px-2.5 py-0.5 text-xs font-semibold text-accent-text">{c}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    );
                  })()}

                  {/* Source insights — reconciliation conflicts, per-field citations, and
                      recommendations all in one collapsible section instead of three separate boxes. */}
                  {(() => {
                    const conflictItems = (gs.review?.items ?? []).filter((item) => item.reason !== 'missing');
                    const hasProvenance = gs.provenance && Object.keys(gs.provenance).length > 0;
                    if (conflictItems.length === 0 && gs.quality?.overall_confidence == null && !hasProvenance && !hasRecommendation) return null;
                    return (
                      <div className="rounded-xl border border-ui-border bg-ui-card p-3 space-y-2">
                        <button
                          type="button"
                          onClick={() => setShowProvenance((v) => !v)}
                          className="flex w-full items-center justify-between text-left"
                        >
                          <span className="text-xs font-semibold text-ui-text">Source insights</span>
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                            className={`flex-shrink-0 text-ui-text3 transition-transform ${showProvenance ? 'rotate-180' : ''}`}>
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </button>

                        {showProvenance && (
                          <div className="space-y-3 border-t border-ui-border pt-2">
                            {/* Reconciliation */}
                            {(conflictItems.length > 0 || gs.quality?.overall_confidence != null) && (
                              <div>
                                <p className="text-micro font-bold uppercase tracking-[0.06em] text-ui-text3 mb-1">Reconciliation</p>
                                {conflictItems.length > 0 ? (
                                  <div className="space-y-0.5">
                                    {conflictItems.map((item, i) => (
                                      <p key={i} className="text-xs text-warning-text">⚠ {item.field}{item.resolution ? ` — ${item.resolution}` : ''}</p>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-xs text-success-text">✓ No conflicts between sources.</p>
                                )}
                                {gs.quality?.overall_confidence != null && (
                                  <p className="text-xs text-ui-text3 mt-0.5">Confidence {(gs.quality.overall_confidence * 100).toFixed(0)}%</p>
                                )}
                              </div>
                            )}

                            {/* Per-field citations */}
                            {hasProvenance && (
                              <div id="assessment-section-sources-reasoning">
                                <p className="text-micro font-bold uppercase tracking-[0.06em] text-ui-text3 mb-1">
                                  Sources &amp; reasoning ({Object.keys(gs.provenance!).length})
                                </p>
                                <div className="space-y-2 divide-y divide-ui-border">
                                  {Object.entries(gs.provenance!).map(([key, p]) => (
                                    <div key={key} className="pt-2 first:pt-0">
                                      <span className="text-xs font-semibold text-ui-text">{provenanceFieldLabel(key)}</span>
                                      {provenanceCitation(p) && (
                                        <p className="text-xs text-ui-text3 truncate">{provenanceCitation(p)}</p>
                                      )}
                                      {p.why && <p className="mt-0.5 text-xs leading-relaxed text-ui-text2">{p.why}</p>}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Recommendations */}
                            {hasRecommendation && (
                              <div>
                                <p className="text-micro font-bold uppercase tracking-[0.06em] text-ui-text3 mb-1">Recommendations</p>
                                <div className="space-y-0.5">
                                  {rec.material_recommendation && <p className="text-xs text-ui-text2">Material: {rec.material_recommendation}</p>}
                                  {rec.color_recommendation && <p className="text-xs text-ui-text2">Color: {rec.color_recommendation}</p>}
                                  {rec.manufacturer_recommendation && <p className="text-xs text-ui-text2">Manufacturer: {rec.manufacturer_recommendation}</p>}
                                  {rec.permit_notes && <p className="text-xs text-ui-text2">Permits: {rec.permit_notes}</p>}
                                  {rec.special_notes && <p className="text-xs text-ui-text2">Special notes: {rec.special_notes}</p>}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </>
              );
            })()}
          </div>
        )}
      </div>
      </div>
      </div>
      )}
      {/* Photo lightbox — full-screen overlay; prev/next; retag + delete from inside. */}
      {lightboxPhotoId && (() => {
        // Navigate within filteredPhotos (not all photos) so prev/next matches what the strip shows.
        const idx = filteredPhotos.findIndex((p) => p.media_id === lightboxPhotoId);
        const photo = idx >= 0 ? filteredPhotos[idx] : null;
        if (!photo) return null;
        const prev = idx > 0 ? filteredPhotos[idx - 1] : null;
        const next = idx < filteredPhotos.length - 1 ? filteredPhotos[idx + 1] : null;
        return (
          <div
            className="fixed inset-0 z-[900] flex flex-col items-center justify-center bg-black/95"
            onClick={() => { setLightboxPhotoId(null); setConfirmDeletePhotoId(null); }}
          >
            {/* Close */}
            <button
              type="button"
              onClick={() => { setLightboxPhotoId(null); setConfirmDeletePhotoId(null); }}
              className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-xl text-white hover:bg-white/20"
            >
              ×
            </button>

            {/* Counter */}
            <p className="absolute top-4 left-1/2 -translate-x-1/2 text-xs font-medium text-white/60">
              {idx + 1} / {filteredPhotos.length}
            </p>

            {/* Prev */}
            {prev && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setLightboxPhotoId(prev.media_id); setConfirmDeletePhotoId(null); }}
                className="absolute left-3 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-lg text-white hover:bg-white/20"
              >
                ‹
              </button>
            )}

            {/* Image */}
            <img
              src={photo.s3_url}
              alt={photo.classifications?.[0] ?? 'photo'}
              className="max-h-[70dvh] max-w-[90vw] rounded-xl object-contain"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Next */}
            {next && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setLightboxPhotoId(next.media_id); setConfirmDeletePhotoId(null); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-lg text-white hover:bg-white/20"
              >
                ›
              </button>
            )}

            {/* Bottom bar — retag + delete */}
            {!readOnly && (
              <div className="absolute bottom-6 left-0 right-0 flex flex-col items-center gap-2 px-4" onClick={(e) => e.stopPropagation()}>
                <div className="flex flex-wrap justify-center gap-1.5">
                  {PHOTO_TAGS.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => void handleRetagPhoto(photo.media_id, tag)}
                      className={`rounded-full px-3 py-1 text-xs font-semibold capitalize ${photo.classifications?.includes(tag) ? 'bg-white text-black' : 'border border-white/30 text-white hover:bg-white/10'}`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
                {confirmDeletePhotoId === photo.media_id ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-white/70">Delete this photo?</span>
                    <button type="button" onClick={() => void handleDeletePhoto(photo.media_id)} className="rounded-lg bg-danger px-3 py-1 text-xs font-semibold text-white">Delete</button>
                    <button type="button" onClick={() => setConfirmDeletePhotoId(null)} className="rounded-lg border border-white/30 px-3 py-1 text-xs text-white">Cancel</button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmDeletePhotoId(photo.media_id)}
                    className="text-xs text-white/50 hover:text-white"
                  >
                    Delete photo
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* Report preview modal */}
      {showReportPreview && assessment?.measurement_report && (
        <div
          className="fixed inset-0 z-[900] flex flex-col bg-black/95"
          onClick={() => setShowReportPreview(false)}
        >
          <div className="flex items-center justify-between px-4 py-3" onClick={(e) => e.stopPropagation()}>
            <p className="text-sm font-semibold text-white truncate max-w-[70%]">
              {assessment.measurement_report.filename || 'Measurement report'}
            </p>
            <div className="flex items-center gap-3">
              <a
                href={assessment.measurement_report.s3_url}
                target="_blank"
                rel="noreferrer"
                className="text-xs font-semibold text-white/70 hover:text-white"
              >
                Open in tab ↗
              </a>
              <button
                type="button"
                onClick={() => setShowReportPreview(false)}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-lg text-white hover:bg-white/20"
              >
                ×
              </button>
            </div>
          </div>
          <div className="flex-1 px-2 pb-4" onClick={(e) => e.stopPropagation()}>
            <iframe
              src={assessment.measurement_report.s3_url}
              title="Measurement report"
              className="h-full w-full rounded-xl border-0"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default AssessmentCaptureCard;
